import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  ApiException,
  BasketClient,
  BasketCreationResponseDto,
  BasketDeliveryRequestDto,
  BasketDeliveryResponseDto,
  BasketSummaryResponseDto,
  CreateOrderCommandResponse,
  CreateOrderRequest,
  EventPricesByCategoryResponseDto,
  GetEventPricesResponse,
  GetOrderTicketTypesRequest,
  GetOrderTicketTypesResponse,
  GetSubscriptionPricesResponse,
  OrderClient,
  PricesClient,
  PrintTicketActionEnumRequestDto,
  PrintingClient,
  SubscriptionPricesByCategoryResponseDto,
  UpdateBasketSeatBeneficiariesPartialSuccessResponseDto
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import { Store, select } from '@ngrx/store';
import * as fromSales from '@root-store/sales-store';
import {
  BasketBeneficiaryItemResponseDto,
  UpdateBasketBeneficiaryRequestDto
} from '@sales/models/basket-beneficiary.model';
import { GroupedBasketSummaryResponseDto } from '@sales/models/sales.model';
import { Observable, catchError, map, of, switchMap, take, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BasketService {
  private readonly basketClient = inject(BasketClient);
  private readonly printingClient = inject(PrintingClient);
  private readonly orderClient = inject(OrderClient);
  private readonly pricesClient = inject(PricesClient);
  private readonly store = inject<Store<fromSales.State>>(Store);
  private readonly httpHandlerService = inject(HttpHandlerService);

  public validateOrder(order: CreateOrderRequest): Observable<CreateOrderCommandResponse> {
    return this.orderClient
      .create(order, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<CreateOrderCommandResponse>());
  }

  public SetDelivery(delivery: BasketDeliveryRequestDto): Observable<BasketDeliveryResponseDto> {
    return this.store.pipe(
      take(1),
      select(fromSales.selectors.selectBasketId),
      switchMap((basketId) =>
        basketId && basketId > 0
          ? this.basketClient
              .addDelivery(delivery, basketId, Constants.ApiVersion)
              .pipe(this.httpHandlerService.handleApiResponse<BasketDeliveryResponseDto>())
          : of(null),
      ),
    );
  }

  public GetDelivery(): Observable<BasketDeliveryResponseDto> {
    return this.store.pipe(
      take(1),
      select(fromSales.selectors.selectBasketId),
      switchMap((basketId) => {
        if (basketId !== undefined && basketId > 0) {
          return this.basketClient
            .getDelivery(basketId, Constants.ApiVersion)
            .pipe(this.httpHandlerService.handleApiResponse<BasketDeliveryResponseDto>());
        } else {
          return of(null);
        }
      }),
    );
  }

  public cancelBasket() {
    return this.store.pipe(
      take(1),
      select(fromSales.selectors.selectBasketId),
      switchMap((basketId) =>
        basketId > 0
          ? this.basketClient
              .cancel(basketId, Constants.ApiVersion)
              .pipe(this.httpHandlerService.handleApiResponse<boolean>())
          : of(false),
      ),
    );
  }

  /**
   * The mediums available for what is in the basket, with the reason each unavailable one is
   * unavailable.
   *
   * Asks the same endpoint as the order path, so the dialog — one component on both opening paths —
   * reads one shape. The alternative is a layout catalogue keyed on the session, which knows no seat
   * and therefore carries no reason: it offers a tile with nothing to explain it, and the operator
   * picks a medium that then renders no ticket at all.
   *
   * Sends the basket LINE keys rather than an event session id: those are what the seats hang off,
   * and the caller already holds them. The channel travels with them because no basket write path
   * records it — it exists only in the front's state until the order is created.
   */
  public getBasketAvailablePrintTypes(
    channelId: number,
    bookings?: GroupedBasketSummaryResponseDto,
  ): Observable<GetOrderTicketTypesResponse> {
    const basketSessionIds = (bookings?.eventSessionItemsGrouped || [])
      .map((booking) => booking?.basketSessionId)
      .filter((id): id is number => id != null);
    const basketSubscriptionIds = (bookings?.subscriptionItemsGrouped || [])
      .map((booking) => booking?.basketSubscriptionId)
      .filter((id): id is number => id != null);

    if (basketSessionIds.length === 0 && basketSubscriptionIds.length === 0) {
      return of({ ticketTypes: [], blockedTicketTypes: [], productSeatCount: 0 });
    }

    const request: GetOrderTicketTypesRequest = {
      action: PrintTicketActionEnumRequestDto.Print,
      basketSessionIds,
      basketSubscriptionIds,
      channelId,
    };

    return this.printingClient
      .getOrderTicketTypes(request, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<GetOrderTicketTypesResponse>());
  }

  public createNewBasket(): Observable<BasketCreationResponseDto> {
    return this.basketClient
      .create(Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<BasketCreationResponseDto>());
  }

  public getBasketSummary(basketId: number): Observable<BasketSummaryResponseDto> {
    return this.basketClient
      .getBasketSummary(basketId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<BasketSummaryResponseDto>());
  }

  public releaseSeatsForCurrentSession() {
    return this.basketClient.releaseSeatsForCurrentSession(Constants.ApiVersion);
  }


  public getBasketSeatBeneficiaries(basketId: number): Observable<BasketBeneficiaryItemResponseDto[]> {
    return this.basketClient
      .getBasketSeatBeneficiaries(basketId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse()) as Observable<BasketBeneficiaryItemResponseDto[]>;
  }

  public getEventPrices(
    channelId: number,
    eventSessionId: number,
    categoryIds: number[],
  ): Observable<EventPricesByCategoryResponseDto[]> {
    return this.pricesClient
      .getEventPrices(channelId, eventSessionId, categoryIds, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventPricesResponse>(),
        map(response => response.prices ?? [])
      );
  }

  public getSubscriptionPrices(
    channelId: number,
    subscriptionId: number,
    categoryIds: number[],
  ): Observable<SubscriptionPricesByCategoryResponseDto[]> {
    return this.pricesClient
      .getSubscriptionPrices(channelId, subscriptionId, categoryIds, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetSubscriptionPricesResponse>(),
        map(response => response.prices ?? [])
      );
  }

  public updateBasketSeatBeneficiaries(
    basketId: number,
    changes: UpdateBasketBeneficiaryRequestDto[],
  ): Observable<UpdateBasketSeatBeneficiariesPartialSuccessResponseDto | null> {
    return this.basketClient
      .updateBasketSeatBeneficiaries(basketId, changes, Constants.ApiVersion)
      .pipe(
        map(() => null),
        catchError((error: ApiException) => {
          if (error.status === 207) {
            return of(error.result as UpdateBasketSeatBeneficiariesPartialSuccessResponseDto);
          }
          return throwError(() => error);
        }),
      );
  }
}
